import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { DEFAULT_CONNECTION_STRATEGY, isConnectionStrategy, type ConnectionStrategy, type Preferences } from '../../../core/ports/preferences-repository';
import { PreferencesService } from '../../../core/services/preferences-service';
import { LocalStoragePreferencesRepository, LOCAL_PREFERENCES_KEY } from '../../../storage/implementations/localstorage-preferences-repository';

const preferencesService = new PreferencesService(new LocalStoragePreferencesRepository());

interface ConnectionStrategyContextValue {
	connectionStrategy: ConnectionStrategy;
	setConnectionStrategy(connectionStrategy: ConnectionStrategy): void;
}

const ConnectionStrategyContext = createContext<ConnectionStrategyContextValue>({
	connectionStrategy: DEFAULT_CONNECTION_STRATEGY,
	setConnectionStrategy: () => undefined,
});

export function ConnectionStrategyProvider({ children }: { children: ReactNode }) {
	const [connectionStrategy, setConnectionStrategyState] = useState<ConnectionStrategy>(() => readStoredConnectionStrategy());

	useEffect(() => {
		let active = true;

		void preferencesService.getConnectionStrategy().then((storedConnectionStrategy) => {
			if (active) {
				setConnectionStrategyState(storedConnectionStrategy);
			}
		});

		return () => {
			active = false;
		};
	}, []);

	const value = useMemo<ConnectionStrategyContextValue>(() => ({
		connectionStrategy,
		setConnectionStrategy(nextConnectionStrategy) {
			setConnectionStrategyState(nextConnectionStrategy);
			void preferencesService.setConnectionStrategy(nextConnectionStrategy);
		},
	}), [connectionStrategy]);

	return <ConnectionStrategyContext.Provider value={value}>{children}</ConnectionStrategyContext.Provider>;
}

export function useConnectionStrategy(): ConnectionStrategyContextValue {
	return useContext(ConnectionStrategyContext);
}

function readStoredConnectionStrategy(): ConnectionStrategy {
	if (typeof localStorage === 'undefined') {
		return DEFAULT_CONNECTION_STRATEGY;
	}

	try {
		const raw = localStorage.getItem(LOCAL_PREFERENCES_KEY);
		if (!raw) {
			return DEFAULT_CONNECTION_STRATEGY;
		}

		const parsed = JSON.parse(raw) as Partial<Preferences>;
		return isConnectionStrategy(parsed.connectionStrategy) ? parsed.connectionStrategy : DEFAULT_CONNECTION_STRATEGY;
	} catch {
		return DEFAULT_CONNECTION_STRATEGY;
	}
}